// useToggleExchangeTokens.ts
import { useCallback, useEffect, useRef } from 'react'

import _ from 'lodash'

import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { LiquiditySource } from '@src/enums'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { IExchangeData } from '@src/types/exchange'
import { createContextLogger } from '@src/utils'
import { Balance } from '@src/utils/balance'
import { isFeatureEnabled } from '@src/utils/environment'
import { returnSanitizedSellAmount } from '@src/utils/sanitizeSellAmount'
import { useTokensStore } from '@src/zustand_stores'

interface UseToggleExchangeTokensProps {
    isWrapOrUnwrapOnly: boolean
    isBuyOutputLoading: boolean
    updateBothData: (sellUpdates: Partial<IExchangeData>, buyUpdates: Partial<IExchangeData>) => void
    sellData: IExchangeData
    buyData: IExchangeData
    setSellAmountString: (amount: string) => void
}

const logger = createContextLogger('useToggleExchangeTokens')

export function useToggleExchangeTokens({
    isWrapOrUnwrapOnly,
    isBuyOutputLoading,
    updateBothData,
    sellData,
    buyData,
    setSellAmountString,
}: UseToggleExchangeTokensProps) {
    const { tokens: zustandTokens } = useTokensStore()

    const { setIsToggleInProgress, setIsBuyOutputLoading, setLiquiditySource } = useExchangeState()

    const onToggleExchangeTokens = useCallback(() => {
        // Much simpler logic if we're wrapping/unwrapping
        logger.info('onToggleExchangeTokens', `isWrapOrUnwrapOnly=${isWrapOrUnwrapOnly}`)
        if (isWrapOrUnwrapOnly) {
            const newBuyData = JSON.parse(JSON.stringify(sellData)) as IExchangeData
            const newSellData = JSON.parse(JSON.stringify(buyData)) as IExchangeData
            updateBothData(newSellData, newBuyData)
            return
        }

        // Set the flag before starting the swap
        setIsToggleInProgress(true)

        // Check if the sell token is wrapped
        const isWrappedToken = sellData.token?.wrapped === true

        // Create new data objects with proper structure
        const newSellValue =
            buyData.token.userBalance !== undefined && buyData.token.userBalance < buyData.value
                ? buyData.token.userBalance
                : buyData.value
        const newSellData = {
            chainId: buyData.chainId,
            token: buyData.token,
            value: newSellValue, // already in correct decimals
        }

        const newBuyData = {
            chainId: sellData.chainId,
            // If the sell token was wrapped, find its native counterpart
            token: isWrappedToken
                ? zustandTokens.find(t => t.chainId === sellData.token.chainId && t.native === true) || sellData.token
                : sellData.token,
            value: sellData.value,
        }

        // Update the sell amount string to reflect the new value
        const newSellAmountString =
            newSellData.value && newSellData.token
                ? new Balance(newSellData.token)
                      .setValue(newSellData.value)
                      .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)
                : ''

        setSellAmountString(returnSanitizedSellAmount(newSellAmountString, newSellData.token.decimals) ?? '')

        // Dispatch updates in sequence
        Promise.resolve()
            .then(() => {
                updateBothData(newSellData, newBuyData)
                return new Promise(resolve => setTimeout(resolve, 0))
            })
            .then(() => {
                setIsToggleInProgress(false)
                setIsBuyOutputLoading(true)
                // Reset liquidity source while loading
                setLiquiditySource(LiquiditySource.unavailable)
            })
    }, [sellData, buyData, zustandTokens, buyData.token.userBalance, isWrapOrUnwrapOnly, sellData.token.userBalance])

    // Create a ref to hold the debounced function
    const debouncedFnRef = useRef<ReturnType<typeof _.debounce>>()

    // Create or update the debounced function when dependencies change
    useEffect(() => {
        debouncedFnRef.current = _.debounce(
            () => {
                onToggleExchangeTokens()
            },
            50,
            { leading: false, trailing: true }
        )

        // Cleanup when dependencies change or component unmounts
        return () => {
            debouncedFnRef.current?.cancel()
        }
    }, [onToggleExchangeTokens])

    // Create a stable function reference that uses the current debounced function
    const debouncedSwapCurrency = useCallback(() => {
        debouncedFnRef.current?.()
    }, [])

    return {
        isToggleInProgress: isBuyOutputLoading,
        onToggleExchangeTokens: debouncedSwapCurrency,
    }
}
